import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Trash2, FileText, ImageIcon, File, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  uploadAttachment,
  deleteAttachment,
  getAttachments,
  getDownloadUrl,
} from '@/services/attachment.service';
import type { Attachment } from '@/data/attachmentTypes';
import { supabase } from '@/lib/supabase';
import { formatFileSize } from '@/data/attachmentTypes';

interface POAttachmentsProps {
  poId: string;
  canUpload: boolean;
}

function fileIcon(mimeType?: string) {
  if (!mimeType) return <File className="h-3.5 w-3.5 shrink-0" />;
  if (mimeType.startsWith('image/')) return <ImageIcon className="h-3.5 w-3.5 shrink-0" />;
  if (mimeType === 'application/pdf') return <FileText className="h-3.5 w-3.5 shrink-0" />;
  return <File className="h-3.5 w-3.5 shrink-0" />;
}

export function POAttachments({ poId, canUpload }: POAttachmentsProps) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAttachments('purchase_order', poId);
      setAttachments(data);
    } catch {
      // silently fail — empty state shown
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id ?? 'unknown';
      await uploadAttachment(file, 'purchase_order', poId, 'other', userId);
      toast.success('Document attached');
      await load();
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    try {
      await deleteAttachment(attachment.id);
      toast.success('Document removed');
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    } catch {
      toast.error('Delete failed — please try again');
    }
  };

  const handleOpen = async (attachment: Attachment) => {
    try {
      const signedUrl = await getDownloadUrl(attachment.id);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Could not open file');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <Paperclip className="h-3.5 w-3.5" />
          Attachments
        </div>
        {canUpload && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={handleUpload}
            />
          </>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground py-1">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">
          No documents attached.{canUpload ? ' Click Upload to add one.' : ''}
        </p>
      ) : (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border border-border/50 px-2 py-1.5 text-xs bg-muted/30"
            >
              <button
                onClick={() => handleOpen(a)}
                className="flex items-center gap-1.5 flex-1 min-w-0 text-left hover:text-primary truncate"
              >
                {fileIcon(a.mimeType)}
                <span className="truncate">{a.fileName}</span>
                {a.fileSize != null && (
                  <span className="text-muted-foreground shrink-0 tabular-nums">
                    {formatFileSize(a.fileSize)}
                  </span>
                )}
              </button>
              {canUpload && (
                <button
                  onClick={() => handleDelete(a)}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  aria-label="Delete attachment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
